/**
 * Client portal routes (`/client/*`) — the external customer audience.
 *
 * Two branches, both top-level: the portal never nests inside `ProtectedLayout`,
 * because that layout renders `AppLayout` — the internal shell, with its sidebar,
 * command palette, calculator and a global messenger SignalR connection. The portal
 * has its own chrome (`ClientLayout`) and its own guard (`ClientRoute`, role-based:
 * a client account receives no permission keys at all, decision Р31).
 *
 *   - `ClientPublicLayout`  → login / forgot-password / reset-password
 *   - `ClientProtectedLayout` → everything behind the session
 *
 * Every page is loaded through `lazyWithRetry(() => import(...))`. The dynamic form is
 * required, not stylistic: a static `import ... from '@/features/client/...'` in this
 * file is an ESLint **error** under the client-portal boundary (direction 1 — nothing
 * outside `features/client/**` may import from it), while the dynamic `import()` inside
 * `lazyWithRetry` is not matched by `no-restricted-imports`.
 *
 * Originally written once, with a placeholder page module for every Р1 screen so later Р1
 * steps could replace page *bodies* without touching the route table. That held only through
 * Р1: Р2 added two genuinely new routes (the dashboard at `/client`, until then a `<Navigate>`
 * to the applications list, and `/client/documents`), and every release since has kept adding
 * routes the same way — most recently Р4's `containers` (list + `:id` card) and `ptos`.
 */

import type { RouteObject } from 'react-router-dom';
import { ROUTES } from '@/constants';
import { CPK } from '@/constants/clientPermissions';
import { ClientPermissionRoute } from '@/router/ClientPermissionRoute';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { ClientProtectedLayout, ClientPublicLayout } from '../layouts';

// ============================================================================
// Lazy-loaded pages
// ============================================================================

// Auth (anonymous)
const ClientLoginPage = lazyWithRetry(() => import('@/features/client/pages/ClientLoginPage'));
const ClientForgotPasswordPage = lazyWithRetry(
  () => import('@/features/client/pages/ClientForgotPasswordPage')
);
const ClientResetPasswordPage = lazyWithRetry(
  () => import('@/features/client/pages/ClientResetPasswordPage')
);

// Portal (authenticated)
const ClientDashboardPage = lazyWithRetry(
  () => import('@/features/client/pages/ClientDashboardPage')
);
const ClientDocumentsPage = lazyWithRetry(
  () => import('@/features/client/pages/ClientDocumentsPage')
);
const ClientApplicationsPage = lazyWithRetry(
  () => import('@/features/client/pages/ClientApplicationsPage')
);
const ClientApplicationCreatePage = lazyWithRetry(
  () => import('@/features/client/pages/ClientApplicationCreatePage')
);
const ClientApplicationDetailsPage = lazyWithRetry(
  () => import('@/features/client/pages/ClientApplicationDetailsPage')
);
const ClientApplicationEditPage = lazyWithRetry(
  () => import('@/features/client/pages/ClientApplicationEditPage')
);
const ClientTemplatesPage = lazyWithRetry(
  () => import('@/features/client/pages/ClientTemplatesPage')
);
const ClientTemplateFormPage = lazyWithRetry(
  () => import('@/features/client/pages/ClientTemplateFormPage')
);
const ClientContainersPage = lazyWithRetry(
  () => import('@/features/client/pages/ClientContainersPage')
);
const ClientContainerDetailsPage = lazyWithRetry(
  () => import('@/features/client/pages/ClientContainerDetailsPage')
);
const ClientPtosPage = lazyWithRetry(() => import('@/features/client/pages/ClientPtosPage'));
const ClientMessengerPage = lazyWithRetry(
  () => import('@/features/client/pages/ClientMessengerPage')
);
const ClientNotificationsPage = lazyWithRetry(
  () => import('@/features/client/pages/ClientNotificationsPage')
);
const ClientProfilePage = lazyWithRetry(() => import('@/features/client/pages/ClientProfilePage'));
// Phone-only sub-screens of «Профиль» (mobile rework 2026-08-21) — each redirects to
// `/client/profile` at ≥768, where the same block is a card of the profile grid.
const ClientProfilePersonalPage = lazyWithRetry(
  () => import('@/features/client/pages/ClientProfilePersonalPage')
);
const ClientProfileCompanyPage = lazyWithRetry(
  () => import('@/features/client/pages/ClientProfileCompanyPage')
);
const ClientProfileColleaguesPage = lazyWithRetry(
  () => import('@/features/client/pages/ClientProfileColleaguesPage')
);
const ClientChangePasswordPage = lazyWithRetry(
  () => import('@/features/client/pages/ClientChangePasswordPage')
);
const ClientEmployeesPage = lazyWithRetry(
  () => import('@/features/client/pages/ClientEmployeesPage')
);
const ClientRolesPage = lazyWithRetry(() => import('@/features/client/pages/ClientRolesPage'));

// ============================================================================
// Routes
// ============================================================================

export const clientRoutes: RouteObject[] = [
  // --------------------------------
  // Anonymous portal screens
  //
  // Under `/client/` like every other portal screen (decision Р16): release Р2 gives
  // the portal its own installable entry point with `scope: '/client/'`, and a login
  // page outside that scope ejects the customer from the installed app on logout.
  // --------------------------------
  {
    element: <ClientPublicLayout />,
    children: [
      {
        path: ROUTES.client.login,
        element: <ClientLoginPage />,
      },
      {
        path: ROUTES.client.forgotPassword,
        element: <ClientForgotPasswordPage />,
      },
      {
        path: ROUTES.client.resetPassword,
        element: <ClientResetPasswordPage />,
      },
    ],
  },

  // --------------------------------
  // Authenticated portal screens
  // --------------------------------
  {
    element: <ClientProtectedLayout />,
    children: [
      {
        // `/client` — the PWA start target, the `scope`/`id` of the portal's own web app
        // manifest and what `getDefaultRoute(UserRole.Client)` returns. Release Р2 put
        // the real dashboard here; until then it was a `<Navigate>` to the applications
        // list, so that the entry point always landed somewhere real.
        path: ROUTES.client.dashboard,
        element: <ClientDashboardPage />,
      },
      {
        path: ROUTES.client.applications.list,
        element: <ClientApplicationsPage />,
      },
      {
        path: ROUTES.client.applications.create,
        element: <ClientApplicationCreatePage />,
      },
      {
        // Перед `details`: `/client/applications/:id` и `/client/applications/:id/edit` — разные
        // шаблоны, но объявлять более специфичный раньше — то же правило, по которому
        // `templateNew` стоит перед `templateEdit`.
        path: ROUTES.client.applications.edit,
        element: <ClientApplicationEditPage />,
      },
      {
        path: ROUTES.client.applications.details,
        element: <ClientApplicationDetailsPage />,
      },
      {
        // Release Р5. Deliberately NOT under `/client/applications` — see the route
        // constant's own comment.
        path: ROUTES.client.templates,
        element: <ClientTemplatesPage />,
      },
      {
        // Форма заготовки на обеих ширинах (27.08.2026) — форма ЗАЯВКИ в режиме шаблона.
        // `new` объявлен ПЕРЕД `:id/edit` не для порядка, а по смыслу: сегмент `new` — не id.
        path: ROUTES.client.templateNew,
        element: <ClientTemplateFormPage />,
      },
      {
        path: ROUTES.client.templateEdit,
        element: <ClientTemplateFormPage />,
      },
      {
        path: ROUTES.client.documents,
        element: <ClientDocumentsPage />,
      },
      {
        // Release Р4. «Мой парк» — the container registry and one container's card.
        //
        // Behind `client.fleet.view` since 27.08.2026. The key had been declared on both sides,
        // granted to three roles by default and shown in «Роли и права» since the matrix shipped,
        // while nothing anywhere read it — unticking «Парк» hid nothing and blocked nothing.
        path: ROUTES.client.containers.list,
        element: (
          <ClientPermissionRoute permission={CPK.Fleet.View}>
            <ClientContainersPage />
          </ClientPermissionRoute>
        ),
      },
      {
        path: ROUTES.client.containers.details,
        element: (
          <ClientPermissionRoute permission={CPK.Fleet.View}>
            <ClientContainerDetailsPage />
          </ClientPermissionRoute>
        ),
      },
      {
        // Release Р4. «ПТО, с которыми работаем» — ranked service points.
        path: ROUTES.client.ptos,
        element: (
          <ClientPermissionRoute permission={CPK.Fleet.View}>
            <ClientPtosPage />
          </ClientPermissionRoute>
        ),
      },
      {
        path: ROUTES.client.messenger,
        element: <ClientMessengerPage />,
      },
      {
        // The SAME element as the list above, deliberately: React keeps the page mounted while the
        // address moves between the two, so the list panel holds its scroll position as somebody
        // walks down a dozen conversations. The conversation inside it remounts on `chatId`.
        path: ROUTES.client.messengerChat,
        element: <ClientMessengerPage />,
      },
      {
        path: ROUTES.client.notifications,
        element: <ClientNotificationsPage />,
      },
      {
        path: ROUTES.client.profile,
        element: <ClientProfilePage />,
      },
      {
        path: ROUTES.client.profilePersonal,
        element: <ClientProfilePersonalPage />,
      },
      {
        path: ROUTES.client.profileCompany,
        element: <ClientProfileCompanyPage />,
      },
      {
        path: ROUTES.client.profileColleagues,
        element: <ClientProfileColleaguesPage />,
      },
      {
        path: ROUTES.client.profilePassword,
        element: <ClientChangePasswordPage />,
      },

      // The company section. Gated on a CLIENT capability, not on a role: a company that hands
      // «Сотрудники» to its administrator and keeps «Роли и права» to itself is the configuration
      // the defaults ship with.
      {
        path: ROUTES.client.employees,
        element: (
          <ClientPermissionRoute permission={CPK.Employees.View}>
            <ClientEmployeesPage />
          </ClientPermissionRoute>
        ),
      },
      {
        path: ROUTES.client.employeeRoles,
        element: (
          <ClientPermissionRoute permission={CPK.Roles.MatrixView}>
            <ClientRolesPage />
          </ClientPermissionRoute>
        ),
      },
    ],
  },
];
